// Sharing a result: the Victory and Roasted screens open the Share screen (App20), which sends the battle's card to
// Telegram, WhatsApp, X, a Telegram story or the phone's photos.
import { api } from "./api.js";
import { go } from "./router.js";
import { tg } from "./telegram.js";

/** What the Share screen is sharing: set by the result screen that opened it. */
export interface ShareContext {
  battleId: string;
  won: boolean;
  arena: string;
  stake: string;
}
const KEY = "dd:share";

export function openShare(c: ShareContext): void {
  sessionStorage.setItem(KEY, JSON.stringify(c));
  go("App20");
}
export function shareContext(): ShareContext | null {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? "null") as ShareContext | null;
  } catch {
    return null;
  }
}

export interface Share {
  id: string;
  /** Our page for the card: WhatsApp and X show the card as its preview. */
  url: string;
  image: string;
  story: string;
  /** Opens the bot straight from Telegram. */
  play: string;
}

/**
 * The battle's card, asked for once. The result screens ask as soon as they show, and the pictures start downloading,
 * so the Share screen opens with the card already there.
 */
const asked = new Map<string, Promise<Share>>();
export function makeShare(battleId: string, showAmount: boolean): Promise<Share> {
  const key = `${battleId}:${showAmount}`;
  let p = asked.get(key);
  if (!p) {
    p = api<Share>("/v1/shares", { method: "POST", body: { battleId, showAmount } });
    p.then((s) => [s.image, s.story].forEach((src) => (new Image().src = src))).catch(() => asked.delete(key));
    asked.set(key, p);
  }
  return p;
}

const open = (url: string) => (tg?.openLink ? tg.openLink(url) : void window.open(url, "_blank"));
const openTelegram = (url: string) => (tg?.openTelegramLink ? tg.openTelegramLink(url) : void window.open(url, "_blank"));

/** Telegram's own chat picker with the card, the caption and a Play button; a plain link share on older apps. */
export async function shareToTelegram(s: Share, caption: string): Promise<void> {
  if (tg?.shareMessage && tg.isVersionAtLeast("8.0")) {
    try {
      const { preparedId } = await api<{ preparedId: string }>(`/v1/shares/${s.id}/telegram`, { method: "POST", body: { caption } });
      tg.shareMessage(preparedId);
      return;
    } catch {
      /* the bot couldn't prepare it: fall back to a link share below */
    }
  }
  openTelegram(`https://t.me/share/url?url=${encodeURIComponent(s.play)}&text=${encodeURIComponent(caption)}`);
}

export function shareToWhatsApp(s: Share, caption: string): void {
  open(`https://wa.me/?text=${encodeURIComponent(`${caption}\n${s.url}`)}`);
}

/**
 * X shows the bot link in the post, and the card picture from our page: X builds the preview from the last link and
 * hides that link once the card shows.
 */
export function shareToX(s: Share, caption: string): void {
  open(`https://x.com/intent/post?text=${encodeURIComponent(`${caption}
${s.play}`)}&url=${encodeURIComponent(s.url)}`);
}

/** Telegram's story editor with the story card. False when this Telegram can't post stories. */
export function shareToStory(s: Share, caption: string): boolean {
  if (!tg?.shareToStory || !tg.isVersionAtLeast("7.8")) return false;
  tg.shareToStory(s.story, { text: caption.slice(0, 200), widget_link: { url: s.play, name: "Play DegenDuel" } });
  return true;
}

/** Saves the card to the phone; on older Telegram it opens the image so it can be saved by hand. */
export function saveImage(url: string, name: string): void {
  if (tg?.downloadFile && tg.isVersionAtLeast("8.0")) tg.downloadFile({ url, file_name: name });
  else open(url);
}
